import type { ComponentPropsWithRef, ReactNode } from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { cn } from "@/lib/utils";

/**
 * One item in a list the attorney works through: a handle and a marker beside
 * it, a caption naming what kind of item it is with any tags, its words, and a
 * row of acts under them, with a menu in the corner.
 *
 * The design draws a claim and its dependents this way. The words stop short of
 * the corner so the menu never sits over them; the row of acts runs under it.
 */
const LEAD_OFFSET = {
  /** A heading item, whose first line sits under a taller caption. */
  roomy: "mt-[17px]",
  dense: "mt-[14px]",
} as const;

export function ItemRow({
  density = "roomy",
  lead,
  marker,
  label,
  tags,
  footerStart,
  footerEnd,
  corner,
  testId,
  className,
  children,
  ...rest
}: Omit<ComponentPropsWithRef<"div">, "children"> & {
  density?: keyof typeof LEAD_OFFSET;
  lead?: ReactNode;
  marker?: ReactNode;
  label: ReactNode;
  tags?: ReactNode;
  footerStart?: ReactNode;
  footerEnd?: ReactNode;
  corner?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  const offset = LEAD_OFFSET[density];
  return (
    <div
      data-testid={testId}
      className={cn("relative flex items-start gap-[9px]", className)}
      {...rest}
    >
      {lead !== undefined && (
        <span className={cn("flex flex-none", offset)}>{lead}</span>
      )}
      {marker !== undefined && (
        <span className={cn("flex flex-none", offset)}>{marker}</span>
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
        <div className="-mb-0.5 flex items-center gap-2 pr-[82px]">
          <MicroLabel size="sm" className="leading-[1.25] font-extrabold">
            {label}
          </MicroLabel>
          {tags}
        </div>
        <div className="w-full pr-[82px]">{children}</div>
        {(footerStart !== undefined || footerEnd !== undefined) && (
          <div className="flex w-full flex-wrap items-center gap-2.5">
            {footerStart}
            <div className="ml-auto flex flex-none items-center gap-2">
              {footerEnd}
            </div>
          </div>
        )}
      </div>
      {corner !== undefined && (
        <div className="absolute top-[9px] right-2.5">{corner}</div>
      )}
    </div>
  );
}
